import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

const CHATBOT_API_BASE = process.env.REACT_APP_CHATBOT_API_BASE as string;

interface Source {
  score: number;
  title: string;
  url: string;
  page: number | null;
  chunk: number | null;
  snippet?: string;
}

interface Message {
  type: 'user' | 'assistant';
  content: string;
}

interface ChatPayload {
  answer?: string;
  sources?: Source[];
  error?: string;
  message?: string;
}

// Normalize lists to split run-on lists into lines
function normalizeLists(md: string): string {
  if (!md) return md;
  let s = md;

  // Convert bullet dots to hyphen bullets
  s = s.replace(/[•‣∙]\s?/g, '- ');

  // Insert a newline before a numbered item if it follows text on the same line
  // e.g., "1. Do this2. Do that" -> "1. Do this\n2. Do that"
  s = s.replace(/(?<!^|\n)(\d+\.\s)/g, '\n$1');

  // Ensure bullet items are on their own lines
  // e.g., "- one - two" -> "- one\n- two"
  s = s.replace(/(?<!^|\n)(-\s)/g, '\n$1');

  // Collapse 3+ blank lines → 2
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

export default function Chat(): JSX.Element {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<Source[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [lastQuery, setLastQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const controllerRef = useRef<AbortController | null>(null);
  // Feedback state
  const [showConfirm, setShowConfirm] = useState(false);
  const [resolved, setResolved] = useState<null | boolean>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const SID = useRef(Math.random().toString(36).slice(2)).current;

  console.log('🚀 ~ Chat ~ detailsOpen:', detailsOpen);
  console.log('🚀 ~ Chat ~ sources:', sources);

  // Submit handoff function
  async function submitHandoff(): Promise<void> {
    try {
      const body = {
        sid: SID,
        user: { email: 'josezx119@gmail.com' },
        messages: messages.map((m) => ({ role: m.type, content: m.content, ts: Date.now() })),
        lastUserMessage: messages[messages.length - 2]?.content || '',
        lastAssistantMessage: messages[messages.length - 1]?.content || '',
        signals: { explicitNo: true },
        metadata: { product: 'EOS', channel: 'web' },
      };

      const res = await fetch(`${CHATBOT_API_BASE}/api/handoff`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('handoff failed');

      // Small toast/notice (keep it minimal; do not change main UI flow)
      const toast = document.createElement('div');
      toast.className =
        'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      toast.textContent = "We'll follow up by email";
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
    } catch (e) {
      // Optional: toast error; do not block normal UX
      console.error('[handoff:ui-error]', e);
    }
  }

  // Submit feedback function
  async function submitFeedback(
    nextResolved: boolean,
    nextRating: number | null = null,
  ): Promise<void> {
    try {
      await fetch(`${CHATBOT_API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sid: SID,
          query: lastQuery,
          resolved: nextResolved,
          rating: nextRating,
          notes,
        }),
      });
    } catch {
      // ignore MVP
    }
  }

  async function ask(): Promise<void> {
    const question = input.trim();
    if (!question) return;

    // Add user message to conversation
    const userMessage = { type: 'user' as const, content: question };
    setMessages((prev) => [...prev, userMessage]);

    // Clear the input immediately (ChatGPT-style)
    setInput('');

    setLoading(true);
    setSources([]);
    setDetailsOpen(false);
    setLastQuery(question);

    controllerRef.current?.abort();
    controllerRef.current = new AbortController();

    let assistantResponse = '';
    try {
      const resp = await fetch(`${CHATBOT_API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: question, sid: SID }),
        signal: controllerRef.current.signal,
      });

      if (!resp.ok) {
        throw new Error(`chat_failed_${resp.status}`);
      }

      const payload = (await resp.json()) as ChatPayload;
      assistantResponse =
        (typeof payload.answer === 'string' && payload.answer.trim().length > 0
          ? payload.answer
          : '') || '';

      if (!assistantResponse) {
        assistantResponse = 'I could not find an answer right now. Please try again.';
      }

      const payloadSources = Array.isArray(payload.sources) ? payload.sources : [];
      setSources(payloadSources.slice(0, 3));
    } catch (err) {
      console.error('[chat:ui-error]', err);
      assistantResponse =
        'I ran into an issue retrieving that information. Please try again in a moment.';
      setSources([]);
    }

    // Add assistant message to conversation
    setMessages((prev) => [...prev, { type: 'assistant', content: assistantResponse }]);
    setLoading(false);

    // Show confirmation bar after streaming finishes
    setShowConfirm(true);
    setResolved(null);
    setRating(null);
    setNotes('');
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void ask();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable messages */}
      <div className="flex-1 space-y-3 p-3 min-h-0 overflow-y-auto">
        {/* Greeting - always show */}
        <div className="message msg-assistant">
          <div className="text-sm text-zinc-700">
            👋 Hi! I&apos;m your Edit on the Spot support assistant. How can I help you today?
          </div>
        </div>

        {/* Conversation history */}
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}
          >
            <div
              className={`max-w-[80%] ${message.type === 'user' ? 'msg-user' : 'msg-assistant'}`}
            >
              <div className="prose prose-zinc max-w-none">
                <ReactMarkdown
                  remarkPlugins={[gfm]}
                  components={{
                    ul: ({ children }: { children?: React.ReactNode }) => (
                      <ul className="list-disc ml-6 my-2 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }: { children?: React.ReactNode }) => (
                      <ol className="list-decimal ml-6 my-2 space-y-1">{children}</ol>
                    ),
                    li: ({ children }: { children?: React.ReactNode }) => (
                      <li className="leading-7">{children}</li>
                    ),
                    p: ({ children }: { children?: React.ReactNode }) => (
                      <p className="my-2">{children}</p>
                    ),
                    a: ({ children, href }: { children?: React.ReactNode; href?: string }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {children}
                      </a>
                    ),
                  }}
                  skipHtml
                >
                  {normalizeLists(message.content)}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        ))}

        {/* Current streaming response */}
        {loading && (
          <div className="flex justify-start mb-4">
            <div className="max-w-[80%] msg-assistant" aria-live="polite">
              <div className="typing-dots" aria-label="Assistant is typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          </div>
        )}

        {/* Sources for the last message */}
        {/* {sources.length > 0 && (
          <div className="mt-4">
            <button className="btn" onClick={() => setDetailsOpen(!detailsOpen)}>
              {detailsOpen ? 'Hide details' : 'Show details'}
            </button>
            {detailsOpen && (
              <div className="details-card">
                <div className="text-sm font-medium mb-2">Top sources</div>
                <ul className="space-y-3">
                  {sources.map((s, i) => (
                    <li key={i} className="text-sm">
                      <div>
                        <span className="font-semibold">{s.title}</span>
                        {s.page ? ` (p.${s.page})` : ''} — score {s.score.toFixed(4)}
                        {s.url ? (
                          <>
                            {' '}
                            —{' '}
                            <a
                              className="text-accent underline"
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              link
                            </a>
                          </>
                        ) : null}
                        {s.chunk != null ? ` — chunk #${s.chunk}` : ''}
                      </div>
                      {s.snippet && (
                        <div className="mt-1 text-zinc-700 leading-6">{s.snippet} …</div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )} */}

        {/* Feedback confirmation bar */}
        {showConfirm && (
          <div className="mt-4 border border-zinc-200 rounded-lg p-3 bg-zinc-50">
            {resolved === null && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Did this solve your issue?</span>
                <button
                  className="btn !py-1.5 !px-3"
                  onClick={() => {
                    setResolved(true);
                    void submitFeedback(true);
                  }}
                >
                  Solved
                </button>
                <button
                  className="btn !py-1.5 !px-3 bg-zinc-300 text-zinc-900 hover:opacity-90"
                  onClick={() => {
                    setResolved(false);
                    void submitHandoff();
                  }}
                >
                  Not yet
                </button>
              </div>
            )}

            {resolved === true && (
              <div className="mt-3">
                <div className="text-sm mb-2">How was the help?</div>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      className={`px-3 py-1 rounded border ${
                        rating === n
                          ? 'bg-accent text-white border-accent'
                          : 'bg-white text-zinc-900 border-zinc-200'
                      }`}
                      onClick={() => {
                        setRating(n);
                        void submitFeedback(true, n);
                        setShowConfirm(false);
                      }}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {resolved === false && (
              <div className="mt-3 space-y-2">
                <div className="text-sm">Tell me what happened (optional):</div>
                <input
                  className="input"
                  placeholder="e.g., Step 3 failed with an error"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    className="btn"
                    onClick={() => {
                      void submitHandoff();
                      setShowConfirm(false);
                    }}
                  >
                    Send
                  </button>
                  <button
                    className="btn bg-zinc-300 text-zinc-900 hover:opacity-90"
                    onClick={() => {
                      setShowConfirm(false);
                    }}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed input at bottom of widget */}
      <div className="flex-shrink-0 border-t border-zinc-200 bg-white p-3">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            className="input"
            placeholder="Ask about tutorials, setup, stream keys, etc."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={loading}
          />
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Asking...' : 'Ask'}
          </button>
        </form>
      </div>
    </div>
  );
}
